import { makeAutoObservable, runInAction } from 'mobx';
import { EmfitAPI } from '../services/BaseAPI';
import { STATUS_API } from 'src/config';

// Role: 0 = Caregiver, 1 = Admin
export type GroupUserRole = 0 | 1;

export interface GroupUser {
  id: number;
  login: string;
  name: string;
  group_id: number;
  phone_number?: string;
  two_factor_enabled: boolean;
  allow_notification_muting: boolean;
  role: GroupUserRole;
  // Lockout state, set by the backend after repeated failed logins
  locked: boolean;
  locked_at: string | null;
  locked_until: string | null;
  login_attempts: number;
  created_at: Date;
  updated_at: Date;
}

export interface CreateGroupUserData {
  name: string;
  login: string;
  password: string;
  group_id: number;
  phone_number?: string;
  two_factor_enabled: boolean;
  allow_notification_muting: boolean;
  role: GroupUserRole;
}

/**
 * Every field the update endpoint accepts must be sent on every update.
 * GroupUserController.update has defaults for the optional ones
 * (allow_notification_muting = true, locked = false, locked_until = null,
 * locked_at = null, login_attempts = 0), so any omitted field is silently
 * reset rather than left alone.
 */
export interface UpdateGroupUserData {
  group_id: number;
  name: string;
  password?: string;
  phone_number?: string;
  two_factor_enabled: boolean;
  allow_notification_muting: boolean;
  role: GroupUserRole;
  locked: boolean;
  locked_at: string | null;
  locked_until: string | null;
  login_attempts: number;
}

/** True when the account is locked and the lock has not yet expired. */
export function isUserLocked(user: GroupUser): boolean {
  if (!user.locked || !user.locked_until) return false;
  return new Date(user.locked_until).getTime() > Date.now();
}

class UsersStoreClass {
  users: GroupUser[] = [];
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchUsers(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const users = await EmfitAPI.get<GroupUser[]>(
        `${STATUS_API}/v1/group-user`
      );
      runInAction(() => {
        this.users = users || [];
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to fetch users';
        this.loading = false;
      });
      console.error('Failed to fetch users:', error);
    }
  }

  async createUser(data: CreateGroupUserData): Promise<GroupUser | null> {
    this.loading = true;
    this.error = null;

    try {
      const newUser = await EmfitAPI.post<GroupUser>(
        `${STATUS_API}/v1/group-user`,
        {
          body: JSON.stringify(data),
        }
      );
      runInAction(() => {
        if (newUser) {
          this.users.push(newUser);
        }
        this.loading = false;
      });
      return newUser;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to create user';
        this.loading = false;
      });
      console.error('Failed to create user:', error);
      return null;
    }
  }

  async updateUser(id: number, data: UpdateGroupUserData): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      const updatedUser = await EmfitAPI.put<GroupUser>(
        `${STATUS_API}/v1/group-user/${id}`,
        {
          body: JSON.stringify(data),
        }
      );
      runInAction(() => {
        if (updatedUser) {
          const index = this.users.findIndex((u) => u.id === id);
          if (index !== -1) {
            this.users[index] = updatedUser;
          }
        }
        this.loading = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to update user';
        this.loading = false;
      });
      console.error('Failed to update user:', error);
      return false;
    }
  }

  /**
   * Clear a lockout imposed after repeated failed logins.
   * Mirrors the reference's Unlock button, which resets the four lock fields
   * through the same update endpoint.
   */
  async unlockUser(user: GroupUser): Promise<boolean> {
    return this.updateUser(user.id, {
      group_id: user.group_id,
      name: user.name,
      phone_number: user.phone_number || undefined,
      two_factor_enabled: user.two_factor_enabled,
      allow_notification_muting: user.allow_notification_muting,
      role: user.role,
      locked: false,
      locked_at: null,
      locked_until: null,
      login_attempts: 0,
    });
  }

  async deleteUser(id: number): Promise<boolean> {
    this.loading = true;
    this.error = null;

    try {
      await EmfitAPI.delete<void>(`${STATUS_API}/v1/group-user/${id}`);
      runInAction(() => {
        this.users = this.users.filter((u) => u.id !== id);
        this.loading = false;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to delete user';
        this.loading = false;
      });
      console.error('Failed to delete user:', error);
      return false;
    }
  }

  get adminCount(): number {
    return this.users.filter((u) => u.role === 1).length;
  }

  canDeleteUser(user: GroupUser): boolean {
    // Cannot delete the last admin
    if (user.role === 1 && this.adminCount === 1) {
      return false;
    }
    return true;
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.users = [];
    this.loading = false;
    this.error = null;
  }
}

export const UsersStore = new UsersStoreClass();
